import { PencilIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAssessmentsOptions } from '@/entities/assessments/api/generated/api';
import {
  type AccumulationTagResponse,
  GetAssessmentsOptionsFieldsItem,
} from '@/entities/assessments/api/generated/model';
import { useAccumulationTagAssignment } from '@/features/assessments/accumulation-tag-assignment/model/useAccumulationTagAssignment';
import { AccumulationTagSelectWithCrud } from '@/features/assessments/accumulation-tag-select/ui/AccumulationTagSelectWithCrud';
import { Button, Skeleton, Text } from '@/shared/ui';
import { TagList } from '@/shared/ui/tag-list/TagList';

const OPTIONS_PARAMS = { fields: [GetAssessmentsOptionsFieldsItem.accumulation_tag] };

type Props = {
  assessmentsAccumulationId: string;
  initialAccumulationTags: AccumulationTagResponse[];
  /** 親で accumulationMappingsData が未取得の placeholder 表示中は true */
  isLoading?: boolean;
};

export const AssessmentComparisonTagSection = ({
  assessmentsAccumulationId,
  initialAccumulationTags,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const initialTagIds = initialAccumulationTags.map((tag) => tag.id);
  const { selectedTagIds, handleChange } = useAccumulationTagAssignment({
    assessmentsAccumulationId,
    initialTagIds,
  });

  // 楽観的に追加された新規タグも表示できるよう、マスタ一覧から名前を引く
  const { data: optionsData, isLoading: isOptionsLoading } =
    useGetAssessmentsOptions(OPTIONS_PARAMS);
  const masterTags = optionsData?.accumulation_tags ?? [];
  const knownTags = masterTags.length > 0 ? masterTags : initialAccumulationTags;
  const assignedTags = knownTags.filter((tag) => selectedTagIds.includes(tag.id));
  const showSkeleton = isLoading || isOptionsLoading;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Text variant="label">{t('assessment.memo.tag')}</Text>
        <Button variant="ghost" onClick={() => setIsEditing((v) => !v)}>
          {isEditing ? (
            t('common.actions.finish-edit')
          ) : (
            <>
              <PencilIcon className="h-4 w-4" />
              {t('common.actions.edit')}
            </>
          )}
        </Button>
      </div>

      {isEditing ? (
        <AccumulationTagSelectWithCrud
          selectedTagIds={selectedTagIds}
          onSelectedTagIdsChange={handleChange}
        />
      ) : showSkeleton ? (
        <Skeleton className="h-6 w-40" />
      ) : assignedTags.length > 0 ? (
        <TagList tags={assignedTags} maxDisplay={assignedTags.length} />
      ) : (
        <Text>{null}</Text>
      )}
    </div>
  );
};
